import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { readRemembered, writeRemembered } from '../../../shared/lib/remembered';
import { isTikTokAccountId, type Account } from '../../../entities/account';
import { fetchMediaComments, fetchTikTokComments } from '../../seeding-report/api/media-comments';
import { loadCommentImages } from '../../seeding-report/lib/comment-images';
import { drawCommentCards } from '../../seeding-report/lib/render-comment-card';
import { makeCommentFilter } from '../../seeding-report/lib/screen-comments';

import { fetchReportImageUrls } from '../api/account-report';

import { buildTopVideosReport, type TopVideosAccount } from '../lib/build-top-videos-report';
import { collectInstagramStats, collectTikTokStats } from '../lib/collect-account-stats';
import { enrichReportRows, usesCaptionHeadline } from '../lib/enrich-report-rows';
import { generateTopVideosDocx } from '../lib/export-top-videos-docx';
import { generateTopVideosGoogleDoc, getTopVideosDocsToken } from '../lib/export-top-videos-gdoc';
import { AccountMultiSelect } from './AccountMultiSelect';
import { DateRangeSelector } from './DateRangeSelector';
import { ExportMenu, FORMAT_DOCX, FORMAT_GDOC } from './ExportMenu';
import { useDateRangeParams } from '../hooks/useDateRangeParams';
import { periodDays } from '../lib/date-range';

// В образце, по которому делался отчёт, пять роликов.
const DEFAULT_TOP = 5;
const MAX_TOP = 50;
// В образце под роликом два-три скриншота комментариев.
const COMMENTS_PER_VIDEO = 3;

/** Перезаливка аватарок и вложений: id → постоянный адрес, с которого холсту можно читать. */
async function fetchAvatarUrls(items: Array<{ id: string; thumbnailUrl: string }>): Promise<Map<string, string>> {
  const urls = new Map<string, string>();
  await fetchReportImageUrls(items, {
    onResult: (r) => {
      if (r.imageUrl) urls.set(r.id, r.imageUrl);
    },
    onProgress: () => {},
  });
  return urls;
}

type Props = {
  /** Instagram и TikTok одним списком — как в селекторе «Статистики аккаунта». */
  accounts: Account[];
};

const REMEMBERED_ACCOUNTS = 'report:top:accounts';

/**
 * Набор аккаунтов при открытии: запомненный; если ссылкой пришёл аккаунт,
 * которого в наборе нет, — только он (человек пришёл со страницы аккаунта).
 */
export function initialTopAccounts(remembered: string[], fromUrl: string | null): string[] {
  if (fromUrl && !remembered.includes(fromUrl)) return [fromUrl];
  return remembered;
}

function accountTitle(account: Account): string {
  return account.username ? `@${account.username}` : account.name;
}

/**
 * Отчёт «Топ видео»: по выбранным аккаунтам за период — N самых
 * просматриваемых роликов, по разделу на аккаунт: новым Google-документом
 * или файлом Word.
 */
export function TopVideosReportCard({ accounts }: Props): JSX.Element {
  const [params] = useSearchParams();
  // Набор аккаунтов и период помнятся: отчёт обычно собирают по одним и тем же.
  const [selectedIds, setSelectedIdsState] = useState<string[]>(() =>
    initialTopAccounts(readRemembered<string[]>(REMEMBERED_ACCOUNTS, []), params.get('account')),
  );
  const setSelectedIds = (ids: string[]) => {
    setSelectedIdsState(ids);
    writeRemembered(REMEMBERED_ACCOUNTS, ids);
  };
  const [periodOption, setPeriodOption] = useDateRangeParams();
  const [topInput, setTopInput] = useState(String(DEFAULT_TOP));
  const [withComments, setWithComments] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [docUrl, setDocUrl] = useState('');

  const topCount = Number.parseInt(topInput, 10);
  const topValid = Number.isInteger(topCount) && topCount >= 1 && topCount <= MAX_TOP;
  // Удалённые с тех пор аккаунты из памяти в отчёт не идут.
  const knownIds = selectedIds.filter((id) => accounts.some((a) => a.id === id));
  const canGenerate = !isGenerating && knownIds.length > 0 && topValid;

  const options = accounts.map((a) => ({
    id: a.id,
    username: a.username,
    name: a.name,
    profilePicture: a.profilePicture,
    platform: a.platform ?? (isTikTokAccountId(a.id) ? 'tiktok' : 'instagram'),
  }));

  async function handleGenerate(output: string): Promise<void> {
    if (!topValid || isGenerating) return;
    const period = periodDays(periodOption);

    // Порядок разделов — как аккаунты стоят в списке, а не как их кликали.
    const byId = new Map(accounts.map((a) => [a.id, a]));
    const chosen = accounts.filter((a) => selectedIds.includes(a.id));
    const targets: TopVideosAccount[] = chosen.map((a) => ({
      id: a.id,
      title: accountTitle(a),
      username: a.username,
      platform: isTikTokAccountId(a.id) ? 'tiktok' : 'instagram',
    }));

    setIsGenerating(true);
    setProgress('');
    setDocUrl('');
    try {
      // Вход в Google — до сбора роликов: окно входа браузер пускает только
      // сразу после клика, а сбор идёт десятки секунд.
      const token = output === 'gdoc' ? await getTopVideosDocsToken() : null;
      let screeningFailed = false;
      const report = await buildTopVideosReport(
        targets,
        period,
        topCount,
        {
          collect: async (target, r) => {
            if (target.platform === 'tiktok') {
              return (await collectTikTokStats(target.id, r)).rows;
            }
            const account = byId.get(target.id);
            if (!account) throw new Error('аккаунт не найден');
            return (await collectInstagramStats(account, r)).rows;
          },
          enrich: enrichReportRows,
          captionHeadline: usesCaptionHeadline,
          comments: withComments
            ? {
                count: COMMENTS_PER_VIDEO,
                fetch: (target, mediaIds) => {
                  const username = target.username ?? '';
                  return target.platform === 'tiktok'
                    ? fetchTikTokComments(mediaIds.map((videoId) => ({ videoId, username })))
                    : fetchMediaComments(mediaIds.map((mediaId) => ({ mediaId, username })));
                },
                // Тот же отсев, что в «Отработано»: негатив, насмешки и
                // неоднозначное в отчёт не идут, сбой проверки — без комментариев.
                filter: makeCommentFilter(COMMENTS_PER_VIDEO, undefined, () => {
                  screeningFailed = true;
                }),
              }
            : undefined,
        },
        setProgress,
      );

      if (report.failed.length > 0) {
        toast.warning(
          `Не удалось собрать: ${report.failed.map((f) => f.title).join(', ')}. Подробности в консоли.`,
        );
      }
      if (report.empty.length > 0) {
        toast.info(`Нет роликов за период: ${report.empty.join(', ')}`);
      }
      if (report.sections.length === 0) {
        toast.error('Отчёт пустой: ни по одному аккаунту роликов не нашлось');
        return;
      }
      if (report.commentErrors.length > 0) {
        console.warn('[Топ видео] комментарии не прочитались:', report.commentErrors);
        toast.warning(`Комментарии не прочитались у роликов: ${report.commentErrors.length}. Подробности в консоли.`);
      }
      if (screeningFailed) {
        toast.warning('Проверка части комментариев не завершилась — под этими роликами блока не будет.');
      }

      const comments = report.sections.flatMap((s) => s.rows.flatMap((r) => r.comments ?? []));
      let cards: ReturnType<typeof drawCommentCards> | undefined;
      if (comments.length > 0) {
        setProgress('Карточки комментариев...');
        const images = await loadCommentImages(comments, { fetchImageUrls: fetchAvatarUrls });
        cards = drawCommentCards(comments, images);
      }

      if (token) {
        const url = await generateTopVideosGoogleDoc({ sections: report.sections, cards }, token, setProgress);
        setDocUrl(url);
        toast.success('Документ создан и открыт в новой вкладке');
      } else {
        setProgress('Формирование файла...');
        await generateTopVideosDocx({ sections: report.sections, cards });
        toast.success('Отчёт сформирован');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось сформировать отчёт');
    } finally {
      setIsGenerating(false);
      setProgress('');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Топ видео</CardTitle>
        <CardDescription>
          Самые просматриваемые ролики выбранных аккаунтов за период — Google-документом или
          файлом Word, по разделу на аккаунт. Под каждым роликом — топовые комментарии карточками в вид
          приложения. У TikTok в строке метрик нет сохранений: площадка их не отдаёт.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Аккаунты</Label>
          <AccountMultiSelect
            accounts={options}
            value={selectedIds}
            onChange={setSelectedIds}
            disabled={isGenerating}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <div className="space-y-2">
            <Label>Период</Label>
            <DateRangeSelector
              value={periodOption}
              onChange={setPeriodOption}
              disabled={isGenerating}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="top-videos-count">Роликов в топе</Label>
            <Input
              id="top-videos-count"
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_TOP}
              value={topInput}
              onChange={(e) => setTopInput(e.target.value)}
              disabled={isGenerating}
              aria-invalid={!topValid}
              className="sm:w-32"
            />
            {!topValid && (
              <p className="text-xs text-destructive">От 1 до {MAX_TOP}</p>
            )}
          </div>
        </div>

        <div className="flex items-start justify-between gap-4 rounded-md border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="top-videos-comments">Топовые комментарии</Label>
            <p className="text-xs text-muted-foreground">
              До {COMMENTS_PER_VIDEO} под каждым роликом, по лайкам среди прошедших проверку. Негатив,
              критику, насмешки и неоднозначные реплики исключаем. Ответы и реплики самого аккаунта не берём.
            </p>
          </div>
          <Switch
            id="top-videos-comments"
            checked={withComments}
            onCheckedChange={setWithComments}
            disabled={isGenerating}
          />
        </div>

        <ExportMenu
          rememberKey="report:top"
          label="Сформировать"
          formats={[FORMAT_GDOC, FORMAT_DOCX]}
          onSelect={handleGenerate}
          disabled={!canGenerate}
          busy={isGenerating}
          busyLabel="Формируем отчёт…"
          className="w-full"
        />

        {isGenerating && progress && (
          <p className="text-sm text-muted-foreground">{progress}</p>
        )}

        {/* Вкладку браузер мог заблокировать — ссылка остаётся под рукой. */}
        {docUrl && !isGenerating && (
          <p className="text-sm">
            Документ готов:{' '}
            <a href={docUrl} target="_blank" rel="noreferrer" className="underline">
              открыть в Google Docs
            </a>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
